import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { bulkContractService } from '../../services/bulkContractService';
import { templateService } from '../../services/templateService';

const BulkAdditionalActModal = ({
  isOpen,
  onClose,
  onSuccess,
  bulkEntry
}) => {
  const [loading, setLoading] = useState(false);

  // Fetch templates
  const { data: templates = [] } = useQuery(
    'templates',
    templateService.getActive,
    {
      enabled: isOpen,
      onError: (error) => {
        console.error('Failed to load templates:', error);
      }
    }
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      templateId: '',
      numberPrefix: 'AA',
      newEndDate: '',
      autoGeneratePdf: true,
      sendToClients: false
    }
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        templateId: '',
        numberPrefix: 'AA',
        newEndDate: '',
        autoGeneratePdf: true,
        sendToClients: false
      });
    }
  }, [isOpen, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const requestData = {
        bulkContractEntryId: bulkEntry.id,
        templateId: data.templateId ? parseInt(data.templateId) : null,
        numberPrefix: data.numberPrefix,
        newEndDate: data.newEndDate,
        autoGeneratePdf: data.autoGeneratePdf,
        sendToClients: data.sendToClients
      };

      await bulkContractService.createBulkAdditionalActs(bulkEntry.id, requestData);
      toast.success(`${bulkEntry.totalContracts} acte adiționale create cu succes!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating bulk additional acts:', error);
      toast.error(error.response?.data?.message || 'Eroare la crearea actelor adiționale în bulk');
    } finally {
      setLoading(false);
    }
  };

  if (!bulkEntry) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Creare Acte Adiționale în Bulk"
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            Se vor crea <span className="font-semibold">{bulkEntry.totalContracts} acte adiționale</span> pentru toate contractele din acest bulk entry.
          </p>
        </div>

        {/* Template Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Template Act Adițional
          </label>
          <select
            {...register('templateId')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Template implicit (Act_Aditional)</option>
            {templates
              .filter(t => t.name?.toLowerCase().includes('aditional') || t.name?.toLowerCase().includes('act'))
              .map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Dacă nu selectezi un template, se va folosi template-ul implicit "Act_Aditional"
          </p>
        </div>

        {/* Number Prefix */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Prefix Număr Act Adițional *
          </label>
          <input
            type="text"
            {...register('numberPrefix', {
              required: 'Prefixul este obligatoriu',
              maxLength: { value: 50, message: 'Prefixul nu poate depăși 50 de caractere' }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="ex: AA, ACT-AD"
          />
          {errors.numberPrefix && (
            <p className="text-red-500 text-xs mt-1">{errors.numberPrefix.message}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Numărul final va fi: {register('numberPrefix').value || 'AA'}-[NumărContract]
          </p>
        </div>

        {/* New End Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nouă Dată de Expirare *
          </label>
          <input
            type="date"
            {...register('newEndDate', {
              required: 'Noua dată de expirare este obligatorie',
              validate: (value) => {
                const selectedDate = new Date(value);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return selectedDate > today || 'Data trebuie să fie în viitor';
              }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.newEndDate && (
            <p className="text-red-500 text-xs mt-1">{errors.newEndDate.message}</p>
          )}
        </div>

        {/* Options */}
        <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
          <label className="flex items-center">
            <input
              type="checkbox"
              {...register('autoGeneratePdf')}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">
              Generează automat PDF-urile
            </span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              {...register('sendToClients')}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">
              Trimite automat actele adiționale către clienți
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Anulează
          </Button>
          <Button
            type="submit"
            loading={loading}
            disabled={loading}
          >
            Creează {bulkEntry.totalContracts} Acte Adiționale
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default BulkAdditionalActModal;